
import ToastMixin from '@/mixins/ToastMixin';
import { mixins } from 'vue-class-component';
import { Component, Emit, Prop } from 'vue-property-decorator';
import { Validations } from 'vuelidate-property-decorators';
import { email, maxLength, required } from 'vuelidate/lib/validators';
import FormInput from '@/components/form/FormInput.vue';
import FormValidationMixin from '@/mixins/FormValidationMixin';
import { EventResponse } from '@/model/Event';
import { InviteInformation } from '@/model/Project';
import { emailLocalPartMaxLength } from '@/mixins/validations/email-validation';
import { ApiError } from '@/model/ApiError';

@Component({
    components: {
        FormInput,
    },
})
export default class InviteForm extends mixins(
    ToastMixin,
    FormValidationMixin,
) {
    @Prop() id!: number;

    @Prop() loading!: boolean;

    @Prop({ default: null }) error!: ApiError | null;

    @Prop({ default: () => [] }) events!: Array<EventResponse>;

    get eventOptions(): Array<{ id: number; name: string }> {
        return (this.events || []).map((event) => {
            return { id: event.id, name: event.name };
        });
    }

    get errorValidationPackage(): string | string[] | undefined {
        return this.error?.validationMessages?.package;
    }

    errorValidationMessage(field: string): string | string[] | undefined {
        return this.error?.validationMessages?.[field];
    }

    lastPointerEvent: MouseEvent | null = null;

    mounted(): void {
        document.addEventListener('blur', this.onDocumentBlur, true);
        document.addEventListener('mousedown', this.onDocumentMouseDown, true);
        document.addEventListener('mousemove', this.onDocumentMouseMove, true);
        this.options = this.events.map((event) => {
            return { id: event.id, name: event.name };
        });
    }

    beforeDestroy(): void {
        document.removeEventListener('blur', this.onDocumentBlur, true);
        document.removeEventListener(
            'mousedown',
            this.onDocumentMouseDown,
            true,
        );
        document.removeEventListener(
            'mousemove',
            this.onDocumentMouseMove,
            true,
        );
    }

    onDocumentBlur = (event: FocusEvent): void => {
        const eventSelector = this.$refs.eventSelector as { $el: HTMLElement };

        if (
            event.target === eventSelector.$el &&
            eventSelector.$el.classList.contains('multiselect--active') &&
            this.lastPointerEvent &&
            this.isScrollbarInteraction(this.lastPointerEvent)
        ) {
            event.stopImmediatePropagation();
        }
    };

    onDocumentMouseDown = (event: MouseEvent): void => {
        this.lastPointerEvent = event;
        const eventSelector = this.$refs.eventSelector as {
            $el: HTMLElement;
            deactivate: () => void;
        };

        if (
            eventSelector.$el.classList.contains('multiselect--active') &&
            !eventSelector.$el.contains(event.target as Node) &&
            !this.isScrollbarInteraction(event)
        ) {
            eventSelector.deactivate();
        }
    };

    onDocumentMouseMove = (event: MouseEvent): void => {
        this.lastPointerEvent = event;
    };

    isScrollbarInteraction(event: MouseEvent): boolean {
        const eventSelector = this.$refs.eventSelector as { $el: HTMLElement };
        const target = event.target as HTMLElement;
        const modal = target.closest('.modal');
        const modalLayers = Array.from(
            document.querySelectorAll<HTMLElement>(
                '.modal.show, .modal.show .modal-dialog, .modal.show .modal-content, .modal.show .modal-body',
            ),
        );
        const scrollableElements = [
            eventSelector.$el.querySelector<HTMLElement>(
                '.multiselect__content-wrapper',
            ),
            modal,
            ...modalLayers,
            document.scrollingElement,
        ].filter(
            (element, index, elements): element is HTMLElement =>
                !!element && elements.indexOf(element) === index,
        );

        return scrollableElements.some((element) => {
            const scrollbarWidth = element.offsetWidth - element.clientWidth;
            if (
                scrollbarWidth <= 0 ||
                element.scrollHeight <= element.clientHeight
            ) {
                return false;
            }

            const rect = element.getBoundingClientRect();
            return (
                event.clientX >= rect.right - scrollbarWidth &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom
            );
        });
    }

    submitted = false;

    formData = {
        email: '',
        events: [],
    };

    @Validations()
    validations = {
        formData: {
            email: {
                required,
                email,
                maxLength: maxLength(80),
                localPartMaxLength: (value: string): boolean =>
                    emailLocalPartMaxLength(value),
            },
        },
    };

    onSubmit(): void {
        this.submitted = true;
        this.$v.formData.$touch();
        if (!!this.id && !this.$v.$invalid) {
            this.submit();
        } else {
            this.fail();
        }
    }

    @Emit('submit')
    submit(): InviteInformation {
        const eventIds = this.formData.events.map(
            (event: { id: number; name: string }) => event.id,
        );

        return { email: this.formData.email, eventIds };
    }

    @Emit()
    fail(): void {
        // failed emit
    }
}
